import React, { useState, useMemo, useEffect, useRef } from 'react';
import './HitsterCard.css';

// QR Code Component using canvas (avoids React peer dependency issues)
const QRCodeCanvas = ({ value, size }) => {
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!value || !canvasRef.current) return;

    const generateQRCode = async () => {
      try {
        // Use dynamic import to load qrcode library
        const QRCode = (await import('qrcode')).default;
        if (canvasRef.current) {
          await QRCode.toCanvas(canvasRef.current, value, {
            width: size,
            margin: 2, // Standard margin - html5-qrcode works better with standard margins
            errorCorrectionLevel: 'M', // Medium error correction - better compatibility with html5-qrcode (H is too dense)
            color: {
              dark: '#000000', // Pure black
              light: '#FFFFFF'  // Pure white - maximum contrast
            },
            // Ensure crisp, sharp rendering for better detection
            rendererOpts: {
              quality: 1
            }
          });
          
          // Apply sharpening to the canvas for better detection
          const ctx = canvasRef.current.getContext('2d');
          if (ctx) {
            // Disable image smoothing for crisp, pixel-perfect QR codes with sharp edges
            ctx.imageSmoothingEnabled = false;
          }
          setError(null);
        }
      } catch (err) {
        console.error('Failed to generate QR code:', err);
        setError(err);
      }
    };

    generateQRCode();
  }, [value, size]);

  if (error) {
    return (
      <div className="hitster-card-no-qr">
        <div style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>Track URI:</div>
        <div style={{ fontSize: '0.75rem', wordBreak: 'break-all', color: '#ccc' }}>{value}</div>
        <div style={{ marginTop: '1rem', fontSize: '0.85rem' }}>QR code error</div>
      </div>
    );
  }

  return (
    <div style={{ 
      background: 'white', 
      borderRadius: '8px', 
      padding: '20px', // Increased padding to create larger quiet zone around QR code
      display: 'inline-block',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)', // Subtle shadow to separate from colored background
      imageRendering: 'crisp-edges' // CSS property to ensure sharp rendering
    }}>
      <canvas 
        ref={canvasRef} 
        style={{ 
          maxWidth: '100%', 
          height: 'auto', 
          display: 'block',
          imageRendering: 'crisp-edges' // Ensure sharp, pixel-perfect rendering (better for QR code scanning)
        }} 
      />
    </div>
  );
};

/**
 * Double-sided Hitster Card Component
 * 
 * Displays a square card with artist, year, and song title on front
 * QR code with track data on back
 * 
 * @param {Object} props
 * @param {string} props.artist - Artist name (displayed at top)
 * @param {string|number} props.year - Year (displayed in large font, centered)
 * @param {string} props.songTitle - Song title (displayed at bottom)
 * @param {string} props.trackUri - Spotify track URI (e.g., "spotify:track:...") for QR code
 * @param {string} props.trackId - Spotify track ID (alternative to trackUri)
 * @param {string} props.color - Background color for the card (optional, defaults to gradient)
 * @param {ReactNode} props.back - Custom content to display on the back (optional, overrides QR code)
 * @param {boolean} props.flipped - Whether the card starts flipped (optional)
 * @param {Function} props.onFlip - Callback when card is flipped (optional)
 * @param {string} props.className - Additional CSS classes (optional)
 * @param {string} props.size - Card size: 'small', 'medium', or 'large' (default: 'medium')
 * @param {string} props.yearConfidence - Year confidence: 'accurate' (green), 'uncertain' (yellow), 'compilation' (red)
 */
function HitsterCard({ 
  artist, 
  year, 
  songTitle, 
  trackUri,
  trackId,
  color, 
  back, 
  flipped: initialFlipped = false, 
  onFlip, 
  className = '',
  size = 'medium',
  yearConfidence = 'uncertain' // 'accurate', 'uncertain', 'compilation'
}) {
  const [isFlipped, setIsFlipped] = useState(initialFlipped);

  // Reset flipped state when initialFlipped prop changes (for new cards)
  useEffect(() => {
    setIsFlipped(initialFlipped);
  }, [initialFlipped]);

  const handleFlip = () => {
    const newFlipped = !isFlipped;
    setIsFlipped(newFlipped);
    if (onFlip) {
      onFlip(newFlipped);
    }
  };

  const frontStyle = color ? { backgroundColor: color } : {};

  // Generate QR code data - prefer trackUri, fallback to trackId, or create from track data
  const qrCodeData = useMemo(() => {
    if (trackUri) {
      return trackUri;
    } else if (trackId) {
      return `spotify:track:${trackId}`;
    } else if (artist && songTitle) {
      // Fallback: create a data URL that can be used to search/play
      const baseUrl = window.location.origin;
      return `${baseUrl}/play?artist=${encodeURIComponent(artist)}&song=${encodeURIComponent(songTitle)}&year=${year || ''}`;
    }
    return null;
  }, [trackUri, trackId, artist, songTitle, year]);

  // Default back content: QR code
  const backContent = back || (qrCodeData ? (
    <div className="hitster-card-qr-container">
      <QRCodeCanvas
        value={qrCodeData}
        size={size === 'small' ? 150 : size === 'large' ? 300 : 200}
      />
      <div className="hitster-card-qr-label">Scan to play</div>
    </div>
  ) : (
    <div className="hitster-card-no-qr">No track data available</div>
  ));

  return (
    <div className={`hitster-card-container hitster-card-${size} ${className}`} onClick={handleFlip}>
      <div className={`hitster-card ${isFlipped ? 'flipped' : ''}`}>
        <div className="hitster-card-front" style={frontStyle}>
          <div className="hitster-card-content">
            <div className="hitster-card-artist">{artist || 'Unknown Artist'}</div>
            <div className={`hitster-card-year hitster-card-year-${yearConfidence}`}>{year || '?'}</div>
            <div className="hitster-card-song">{songTitle || 'Unknown Song'}</div>
          </div>
        </div>
        <div className="hitster-card-back">
          <div className="hitster-card-content">
            {backContent}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HitsterCard;

